import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    ProductDTO,
    saveProductApi,
    useDeleteProductApi,
    useSearchProductApi,
} from '../../../api/product-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveProductModal } from './save-product-modal';

export function ProductApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<ProductDTO | undefined>();
    const productInit = useSearchProductApi();
    const deleteProduct = useDeleteProductApi();

    const { result, error, busy, initialized, initializing } = productInit;

    const columns: IBasicTableColumn<ProductDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 20,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>name</T>,
                name: 'name',
            },
            {
                Header: <T>category</T>,
                name: 'categoryCode',
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        productInit.post(null, {});
    }, [initialized, initializing, error, productInit]);

    const handleDelete = (product: ProductDTO) => {
        bd.msgbox(
            translate('delete-product'),
            translate('confirm-delete-product'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteProduct.remove(
                            product,
                            () => {
                                productInit.updateResult({
                                    ...result,
                                    products: result.products.filter(b => b.id !== product.id),
                                });
                                notify.dark(translate('selected-product-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedProduct(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (product: ProductDTO) => {
        saveProductApi(product)
            .then(res => {
                if (selectedProduct) {
                    productInit.updateResult({
                        ...result,
                        products: result.products.map(b => (b.id === product.id ? res.product : b)),
                    });
                } else {
                    productInit.updateResult({
                        ...result,
                        products: [res.product, ...result.products],
                    });
                }

                setShowModal(false);
                setSelectedProduct(undefined);
                notify.success(
                    selectedProduct
                        ? translate('selected-product-updated-successfully')
                        : translate('new-product-created-successfully')
                );
            })
            .catch(notify.error);
    };

    if (error) return <InitFailed ex={error} onRetry={() => productInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="products"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-product</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedProduct(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        cellCSS={({ row }) => {
                            if (!row.enable) return 'bg-gray-100 opacity-50';
                            return '';
                        }}
                        columns={columns}
                        data={result.products}
                        onRowClick={({ row }) => {
                            setSelectedProduct(row);
                            setShowModal(true);
                        }}
                        hover
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <SaveProductModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedProduct(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        categories={result.categories}
                        selectedProduct={selectedProduct}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
